<template>
  <SubmissionViewer
    v-if="selectedSubmission"
    :submission="selectedSubmission"
    :admin-id="adminId"
    :admin-name="adminName"
    @cancel="selectedSubmission = null"
    @reviewed="handleSubmissionStatusSaved"
    @status-saved="handleSubmissionStatusSaved"
  />

  <section v-else class="submissions-window">
    <div class="submissions-toolbar">
      <div class="toolbar-primary">
        <label class="submission-search">
          <span class="material-icons-outlined" aria-hidden="true">
            search
          </span>

          <input
            v-model="searchText"
            type="search"
            placeholder="Search submissions"
            aria-label="Search Litigation submissions"
          />
        </label>

        <button
          type="button"
          class="refresh-button"
          :disabled="loading"
          @click="loadSubmissions"
        >
          {{ loading ? "Loading..." : "Refresh" }}
        </button>
      </div>

      <div class="toolbar-filters">
        <select
          v-model="statusFilter"
          class="status-filter"
          aria-label="Filter submissions by status"
        >
          <option value="">All statuses</option>
          <option
            v-for="status in workflowStatuses"
            :key="status.value"
            :value="status.value"
          >
            {{ status.label }}
          </option>
        </select>

        <select
          v-model="activityFilter"
          class="activity-filter"
          aria-label="Filter submissions by client updates"
        >
          <option value="">All activity</option>
          <option value="updated">Needs review or updated</option>
        </select>

        <select
          v-model="groupBy"
          class="group-filter"
          aria-label="Group submissions"
        >
          <option value="date">Group by date</option>
          <option value="litigation">Group by litigation</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="state-card">
      Loading Litigation submissions...
    </div>

    <div v-else-if="errorMessage" class="state-card error">
      {{ errorMessage }}
    </div>

    <div v-else-if="submissions.length === 0" class="state-card">
      No completed Litigation submissions were found.
    </div>

    <div v-else-if="submissionGroups.length === 0" class="state-card">
      No Litigation submissions match the selected filters.
    </div>

    <div v-else class="submission-groups">
      <section
        v-for="group in submissionGroups"
        :key="group.id"
        class="submission-group"
      >
        <button
          v-if="group.kind === 'litigation'"
          type="button"
          class="litigation-group-heading"
          :class="{ expandable: group.collapsible }"
          :disabled="!group.collapsible"
          :aria-expanded="isGroupExpanded(group)"
          @click="toggleLitigationGroup(group)"
        >
          <div>
            <h3>{{ group.title }}</h3>
            <small v-if="group.subtitle">{{ group.subtitle }}</small>
          </div>

          <span class="litigation-group-count">
            {{ group.submissions.length }}
          </span>

          <span
            v-if="group.collapsible"
            class="material-icons-outlined litigation-group-chevron"
            aria-hidden="true"
          >
            {{ isGroupExpanded(group) ? "expand_less" : "expand_more" }}
          </span>
        </button>

        <div v-else class="group-heading">
          <h3>{{ group.title }}</h3>
          <span>{{ group.submissions.length }}</span>
        </div>

        <div v-if="isGroupExpanded(group)" class="submission-table">
          <div class="submission-row table-heading" aria-hidden="true">
            <span>Litigation</span>
            <span>Answers</span>
            <span>Completed</span>
            <span>Status</span>
          </div>

          <button
            v-for="submission in group.submissions"
            :key="submission.id"
            type="button"
            class="submission-row"
            @click="selectedSubmission = submission"
          >
            <div class="litigation-summary">
              <strong>{{ submission.publicActionName }}</strong>
              <span v-if="submission.internalName">
                {{ submission.internalName }}
              </span>
            </div>

            <span class="answer-count">
              {{ submission.answerCount ?? "—" }}
            </span>

            <time :datetime="completedDateTime(submission.completedAt)">
              {{ formattedCompletion(submission.completedAt) }}
            </time>

            <div class="status-summary">
              <span class="status-pill" :class="submission.status">
                {{ statusLabel(submission.status) }}
              </span>
              <span v-if="isUpdatedSubmission(submission)" class="updated-pill">
                {{ submission.needsReview ? "Needs Review" : "Updated" }}
              </span>
            </div>
          </button>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { computed, onMounted, ref } from "vue";

import { fetchLitigationSubmissions } from "@/firebase/firestore";
import SubmissionViewer from "@/components/submissions/SubmissionViewer.vue";
import {
  litigationSubmissionWorkflowStatuses,
  litigationSubmissionWorkflowStatusLabel
} from "@/models/litigationSubmissions";

export default {
  name: "SubmissionsListWindow",

  components: {
    SubmissionViewer
  },

  emits: ["new-count"],
  props: {
    initialStatus: { type: String, default: "" },
    adminId: {
      type: String,
      default: ""
    },

    adminName: {
      type: String,
      default: ""
    }
  },

  setup(props, { emit }) {
    const submissions = ref([]);
    const loading = ref(false);
    const errorMessage = ref("");
    const searchText = ref("");
    const statusFilter = ref(props.initialStatus);
    const activityFilter = ref("");
    const groupBy = ref("date");
    const expandedLitigationGroupIds = ref(new Set());
    const selectedSubmission = ref(null);
    const workflowStatuses = litigationSubmissionWorkflowStatuses;

    const firestoreDate = value => {
      if (value?.toDate) { return value.toDate(); }
      if (value instanceof Date) { return value; }

      return new Date(value ?? 0);
    };

    const isUpdatedSubmission = submission => {
      if (submission?.needsReview === true) { return true; }
      if (!submission?.editedOnDate) { return false; }

      const editedOnDate = firestoreDate(submission.editedOnDate);

      if (!submission.lastReviewedDate) { return true; }

      return editedOnDate > firestoreDate(submission.lastReviewedDate);
    };

    const filteredSubmissions = computed(() => {
      return submissions.value.filter(submission => {
        const matchesStatus =
          !statusFilter.value || submission.status === statusFilter.value;
        const matchesActivity =
          activityFilter.value !== "updated" || isUpdatedSubmission(submission);

        return matchesStatus && matchesActivity;
      });
    });

    const dateSubmissionGroups = computed(() => {
      const now = new Date();
      const startOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());

      const groups = [
        { id: "today", kind: "date", title: "Today", submissions: [] },
        { id: "this-week", kind: "date", title: "This Week", submissions: [] },
        { id: "older", kind: "date", title: "Older", submissions: [] }
      ];

      filteredSubmissions.value.forEach(submission => {
        const completedAt = firestoreDate(submission.completedAt);

        if (completedAt >= startOfToday) {
          groups[0].submissions.push(submission);
        } else if (completedAt >= startOfWeek) {
          groups[1].submissions.push(submission);
        } else {
          groups[2].submissions.push(submission);
        }
      });

      return groups.filter(group => group.submissions.length > 0);
    });

    const litigationSubmissionGroups = computed(() => {
      const groupsByLitigation = new Map();

      filteredSubmissions.value.forEach(submission => {
        const litigationKey = submission.litigationId || submission.publicActionName;
        const existingGroup = groupsByLitigation.get(litigationKey);

        if (existingGroup) {
          existingGroup.submissions.push(submission);
          return;
        }

        groupsByLitigation.set(litigationKey, {
          id: `litigation-${litigationKey}`,
          kind: "litigation",
          title: submission.publicActionName || "Litigation Submission",
          subtitle: submission.internalName || "",
          submissions: [submission]
        });
      });

      return Array.from(groupsByLitigation.values())
        .map(group => ({
          ...group,
          collapsible: group.submissions.length > 1,
          submissions: group.submissions.slice().sort(
            (first, second) =>
              firestoreDate(second.completedAt) - firestoreDate(first.completedAt)
          )
        }))
        .sort((first, second) => first.title.localeCompare(second.title));
    });

    const submissionGroups = computed(() =>
      groupBy.value === "litigation"
        ? litigationSubmissionGroups.value
        : dateSubmissionGroups.value
    );

    const isGroupExpanded = group =>
      !group.collapsible || expandedLitigationGroupIds.value.has(group.id);

    const toggleLitigationGroup = group => {
      if (!group.collapsible) { return; }

      const nextExpandedIds = new Set(expandedLitigationGroupIds.value);

      if (nextExpandedIds.has(group.id)) {
        nextExpandedIds.delete(group.id);
      } else {
        nextExpandedIds.add(group.id);
      }

      expandedLitigationGroupIds.value = nextExpandedIds;
    };

    const formattedCompletion = value => {
      const date = firestoreDate(value);

      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
      }).format(date);
    };

    const completedDateTime = value => firestoreDate(value).toISOString();

    const statusLabel = litigationSubmissionWorkflowStatusLabel;

    const loadSubmissions = async () => {
      loading.value = true;
      errorMessage.value = "";

      try {
        submissions.value = await fetchLitigationSubmissions();
        emit("new-count", submissions.value.filter(row => row.status === "new").length);
      } catch (error) {
        console.error("❌ Unable to load Litigation submissions:", error);
        errorMessage.value = "Unable to load Litigation submissions.";
      } finally {
        loading.value = false;
      }
    };

    const handleSubmissionStatusSaved = updatedSubmission => {
      submissions.value = submissions.value.map(submission =>
        submission.id === updatedSubmission.id ? updatedSubmission : submission
      );
      selectedSubmission.value = updatedSubmission;
      emit("new-count", submissions.value.filter(row => row.status === "new").length);
    };

    onMounted(loadSubmissions);

    return {
      submissions,
      filteredSubmissions,
      submissionGroups,
      loading,
      errorMessage,
      searchText,
      statusFilter,
      activityFilter,
      groupBy,
      selectedSubmission,
      workflowStatuses,
      isUpdatedSubmission,
      isGroupExpanded,
      toggleLitigationGroup,
      handleSubmissionStatusSaved,
      loadSubmissions,
      formattedCompletion,
      completedDateTime,
      statusLabel
    };
  }
};
</script>

<style scoped>
.submissions-window {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.submissions-toolbar {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
}

.toolbar-primary {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) auto;
  gap: 12px;
}

.toolbar-filters {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.submission-search {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 0 14px;
  border: 1px solid #d8dedb;
  border-radius: 10px;
  background: #ffffff;
  color: #7b8580;
}

.submission-search input {
  width: 100%;
  padding: 12px 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #18231f;
  font: inherit;
}

.status-filter,
.activity-filter,
.group-filter,
.refresh-button {
  padding: 12px 14px;
  border: 1px solid #d8dedb;
  border-radius: 10px;
  background: #ffffff;
  color: #29342f;
  font: inherit;
}

.refresh-button {
  min-width: 130px;
  font-weight: 600;
  cursor: pointer;
}

.refresh-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.state-card {
  padding: 24px;
  border: 1px solid #dde2df;
  border-radius: 12px;
  background: #ffffff;
  color: #66716b;
  text-align: center;
}

.state-card.error {
  border-color: #edc4c4;
  color: #a33636;
}

.submission-groups {
  min-height: 0;
  padding-right: 4px;
  overflow-y: auto;
}

.submission-group + .submission-group {
  margin-top: 26px;
}

.group-heading {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 9px;
}

.group-heading h3 {
  margin: 0;
  color: #68736d;
  font-size: 0.74rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.group-heading span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #edf4ef;
  color: #178554;
  font-size: 0.72rem;
  font-weight: 700;
}

.litigation-group-heading {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 0 0 9px;
  padding: 15px 17px;
  border: 1px solid #dde2df;
  border-radius: 11px;
  background: #ffffff;
  color: inherit;
  font: inherit;
  text-align: left;
}

.litigation-group-heading.expandable {
  grid-template-columns: minmax(0, 1fr) auto auto;
  cursor: pointer;
}

.litigation-group-heading.expandable:hover {
  border-color: #bcdacb;
  background: #f5faf7;
}

.litigation-group-heading:disabled {
  opacity: 1;
}

.litigation-group-heading h3 {
  margin: 0;
  color: #1d2924;
  font-size: 0.96rem;
}

.litigation-group-heading small {
  display: block;
  margin-top: 4px;
  color: #78827d;
  font-size: 0.8rem;
}

.litigation-group-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 14px;
  background: #edf4ef;
  color: #178554;
  font-size: 0.76rem;
  font-weight: 700;
}

.litigation-group-chevron {
  color: #68736d;
  font-size: 1.35rem;
}

.submission-table {
  overflow: hidden;
  border: 1px solid #dde2df;
  border-radius: 12px;
  background: #ffffff;
}

.submission-row {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 100px 220px 120px;
  align-items: center;
  gap: 18px;
  padding: 17px 20px;
  width: 100%;
  border: 0;
  background: #ffffff;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.submission-row:not(.table-heading):hover {
  background: #f5faf7;
}

.submission-row + .submission-row {
  border-top: 1px solid #edf0ee;
}

.submission-row.table-heading {
  padding-top: 12px;
  padding-bottom: 12px;
  background: #f7f9f8;
  color: #5f6a64;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.litigation-summary {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.litigation-summary strong,
.litigation-summary span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.litigation-summary strong {
  color: #1d2924;
  font-size: 0.97rem;
}

.litigation-summary span,
.submission-row time {
  margin-top: 4px;
  color: #78827d;
  font-size: 0.83rem;
}

.answer-count {
  color: #35413b;
  font-weight: 600;
}

.status-pill {
  justify-self: start;
  padding: 7px 11px;
  border-radius: 999px;
  background: #eaf6ef;
  color: #178554;
  font-size: 0.78rem;
  font-weight: 700;
}

.status-summary {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 5px;
}

.updated-pill {
  padding: 4px 8px;
  border-radius: 999px;
  background: #fff5d8;
  color: #8b6510;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.status-pill.inReview {
  background: #fff2e7;
  color: #ad5514;
}

.status-pill.needsInformation {
  background: #fff5d8;
  color: #8b6510;
}

.status-pill.readyForReferral {
  background: #eaf1fb;
  color: #315d91;
}

.status-pill.referralInProgress {
  background: #eee9fb;
  color: #65479b;
}

.status-pill.placedWithPartner {
  background: #e6f6ef;
  color: #16794d;
}

.status-pill.retainedInHouse {
  background: #e6f3f7;
  color: #22667a;
}

.status-pill.notMovingForward,
.status-pill.closed {
  background: #eef0ef;
  color: #66716b;
}

@media (max-width: 1000px) {
  .submission-row {
    grid-template-columns: minmax(220px, 1fr) 80px 180px 100px;
    gap: 12px;
  }
}

@media (max-width: 760px) {
  .toolbar-primary,
  .toolbar-filters {
    grid-template-columns: 1fr;
  }

  .submission-table {
    overflow-x: auto;
  }

  .submission-row {
    min-width: 720px;
  }
}
</style>
